import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import styles from './staffNotification.module.scss'
import Title from '@/components/_custom/Title/title'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { MailOutline, VibrationOutlined } from '@mui/icons-material'
import TableRowPreferences from './NotificationTableRow'
import { DaycareType } from '@/types/Daycare'
import { CHANGE_LINK_NOTIFS } from '@/modules/API/daycare/staffLinkHandler'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

const StaffNotification: FunctionComponent = () => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const { user, fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [notifReport, setNotifReport] = useState<boolean>(false)

    const [notifNewParent, setNotifNewParent] = useState<boolean>(false)
    const [notifPushNewParent, setNotifPushNewParent] = useState<boolean>(false)

    const [notifUpdateParent, setNotifUpdateParent] = useState<boolean>(false)
    const [notifPushUpdateParent, setNotifPushUpdateParent] = useState<boolean>(false)

    const [notifNewStaff, setNotifNewStaff] = useState<boolean>(false)
    const [notifPushNewStaff, setNotifPushNewStaff] = useState<boolean>(false)

    const [notifConfirmation, setNotifConfirmation] = useState<boolean>(false)
    const [notifPushConfirmation, setNotifPushConfirmation] = useState<boolean>(false)

    const [notifCancellation, setNotifCancellation] = useState<boolean>(false)
    const [notifPushCancellation, setNotifPushCancellation] = useState<boolean>(false)

    const [notifRequestSlot, setNotifRequestSlot] = useState<boolean>(false)
    const [notifPushRequestSlot, setNotifPushRequestSlot] = useState<boolean>(false)

    const [notifEventDays, setNotifEventDays] = useState<boolean>(false)
    const [notifPushEventDays, setNotifPushEventDays] = useState<boolean>(false)

    const [notifRegularSlot, setNotifRegularSlot] = useState<boolean>(false)
    const [notifPushRegularSlot, setNotifPushRegularSlot] = useState<boolean>(false)

    const [notifTaskNote, setNotifTaskNote] = useState<boolean>(true)
    const [notifPushTaskNote, setNotifPushTaskNote] = useState<boolean>(true)

    const [updateStaffNotifs, { loading: loadingStaffNotifs }] = useMutation(CHANGE_LINK_NOTIFS, {
        variables: {
            daycare_id: user?.currentDaycare?.id,
            notifReport,
            notifNewParent,
            notifPushNewParent,
            notifUpdateParent,
            notifPushUpdateParent,
            notifNewStaff,
            notifPushNewStaff,
            notifConfirmation,
            notifPushConfirmation,
            notifCancellation,
            notifPushCancellation,
            notifRequestSlot,
            notifPushRequestSlot,
            notifEventDays,
            notifPushEventDays,
            notifRegularSlot,
            notifPushRegularSlot,
            notifTaskNote,
            notifPushTaskNote
        },
        onCompleted: (): void => {
            setStaffNotifsWasUpdated(false)
            showNotificationSuccess(t('notifUpdate'))
        },
        onError: (): void => {
            setStaffNotifsWasUpdated(false)
            fetchCurrentDaycare()
            showNotificationError(t('notifUpdateError'))
        }
    })

    const [staffNotifsWasUpdated, setStaffNotifsWasUpdated] = useState<boolean>(false)

    useEffect(() => {
        if (user?.currentDaycare && !loadingStaffNotifs && staffNotifsWasUpdated) updateStaffNotifs()
    }, [staffNotifsWasUpdated])

    useEffect(() => {
        setNotifReport(user?.currentDaycare?.staffLink?.notifReport || false)
        setNotifNewParent(user?.currentDaycare?.staffLink?.notifNewParent || false)
        setNotifPushNewParent(user?.currentDaycare?.staffLink?.notifPushNewParent || false)
        setNotifUpdateParent(user?.currentDaycare?.staffLink?.notifUpdateParent || false)
        setNotifPushUpdateParent(user?.currentDaycare?.staffLink?.notifPushUpdateParent || false)
        setNotifNewStaff(user?.currentDaycare?.staffLink?.notifNewStaff || false)
        setNotifPushNewStaff(user?.currentDaycare?.staffLink?.notifPushNewStaff || false)
        setNotifConfirmation(user?.currentDaycare?.staffLink?.notifConfirmation || false)
        setNotifPushConfirmation(user?.currentDaycare?.staffLink?.notifPushConfirmation || false)
        setNotifCancellation(user?.currentDaycare?.staffLink?.notifCancellation || false)
        setNotifPushCancellation(user?.currentDaycare?.staffLink?.notifPushCancellation || false)
        setNotifRequestSlot(user?.currentDaycare?.staffLink?.notifRequestSlot || false)
        setNotifPushRequestSlot(user?.currentDaycare?.staffLink?.notifPushRequestSlot || false)
        setNotifEventDays(user?.currentDaycare?.staffLink?.notifEventDays || false)
        setNotifPushEventDays(user?.currentDaycare?.staffLink?.notifPushEventDays || false)
        setNotifRegularSlot(user?.currentDaycare?.staffLink?.notifRegularSlot || false)
        setNotifPushRegularSlot(user?.currentDaycare?.staffLink?.notifPushRegularSlot || false)
        setNotifTaskNote(user?.currentDaycare?.staffLink?.notifTaskNote || false)
        setNotifPushTaskNote(user?.currentDaycare?.staffLink?.notifPushTaskNote || false)
    }, [user?.currentDaycare])

    async function backToHome(): Promise<void> {
        await fetchCurrentDaycare()
        navigate('/home')
    }

    return (
        <>
            <div className={styles.PagePosition}>
                {user && user?.daycares.length > 1 && (
                    <div className={styles.HeaderRow}>
                        <div className={styles.MultipleSections}>
                            <DaycareSelector />
                        </div>
                    </div>
                )}
                <div className={styles.NotificationPlacement}>
                    <div className={styles.Form}>
                        <Title text={t('myAlerts')} className={styles.Title} />
                        <Table className={styles.TableComponent} stickyHeader={false}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '60%' }}></TableCell>
                                    <TableCell sx={{ width: '20%' }} align={'center'}>
                                        <MailOutline />
                                    </TableCell>
                                    <TableCell sx={{ width: '20%' }} align={'center'}>
                                        <VibrationOutlined />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRowPreferences
                                    legend={
                                        user?.currentDaycare?.type === DaycareType.laep ||
                                        user?.currentDaycare?.type === DaycareType.rpe
                                            ? t('NotifNewPublic')
                                            : t('notifNewParent')
                                    }
                                    mailNotification={notifNewParent}
                                    setNotifMail={setNotifNewParent}
                                    pushNotification={notifPushNewParent}
                                    setNotifPush={setNotifPushNewParent}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={
                                        user?.currentDaycare?.type === DaycareType.laep ||
                                        user?.currentDaycare?.type === DaycareType.rpe
                                            ? t('updatedPublic')
                                            : t('updatedParent')
                                    }
                                    mailNotification={notifUpdateParent}
                                    setNotifMail={setNotifUpdateParent}
                                    pushNotification={notifPushUpdateParent}
                                    setNotifPush={setNotifPushUpdateParent}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={t('newStaff')}
                                    mailNotification={notifNewStaff}
                                    setNotifMail={setNotifNewStaff}
                                    pushNotification={notifPushNewStaff}
                                    setNotifPush={setNotifPushNewStaff}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={t('bookingSlots')}
                                    mailNotification={notifConfirmation}
                                    setNotifMail={setNotifConfirmation}
                                    pushNotification={notifPushConfirmation}
                                    setNotifPush={setNotifPushConfirmation}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={t('slotCancellation')}
                                    mailNotification={notifCancellation}
                                    setNotifMail={setNotifCancellation}
                                    pushNotification={notifPushCancellation}
                                    setNotifPush={setNotifPushCancellation}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={t('regularSlot')}
                                    mailNotification={notifRegularSlot}
                                    setNotifMail={setNotifRegularSlot}
                                    pushNotification={notifPushRegularSlot}
                                    setNotifPush={setNotifPushRegularSlot}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={t('requestSlot')}
                                    mailNotification={notifRequestSlot}
                                    setNotifMail={setNotifRequestSlot}
                                    pushNotification={notifPushRequestSlot}
                                    setNotifPush={setNotifPushRequestSlot}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={t('events')}
                                    mailNotification={notifEventDays}
                                    setNotifMail={setNotifEventDays}
                                    pushNotification={notifPushEventDays}
                                    setNotifPush={setNotifPushEventDays}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={t('tasksAndNotes')}
                                    mailNotification={notifTaskNote}
                                    setNotifMail={setNotifTaskNote}
                                    pushNotification={notifPushTaskNote}
                                    setNotifPush={setNotifPushTaskNote}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                                <TableRowPreferences
                                    legend={t('monthlyReport')}
                                    mailNotification={notifReport}
                                    setNotifMail={setNotifReport}
                                    setStaffNotifsWasUpdated={setStaffNotifsWasUpdated}
                                />
                            </TableBody>
                        </Table>
                    </div>
                    <ButtonTextIcon type={'return'} onClick={backToHome} className={styles.Button} />
                </div>
            </div>
        </>
    )
}

export default StaffNotification
